import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface FacilityLocationFormData {
  facility_id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  type: string;
  contact_name: string;
  email: string;
  phone: string;
  contact_phone_ext: string;
  service_hours_from: string;
  service_hours_to: string;
  number_of_docks: string;
  notes: string;
  stop_instructions: string;
}

interface FacilityLocationFormProps {
  facilityId?: string;
  onClose: () => void;
  onSuccess: () => void;
  isModal?: boolean;
}

export function FacilityLocationForm({ facilityId, onClose, onSuccess, isModal = true }: FacilityLocationFormProps) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!facilityId);
  
  const [formData, setFormData] = useState<FacilityLocationFormData>({
    facility_id: `F-${Date.now()}`,
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    type: 'Warehouse',
    contact_name: '',
    email: '',
    phone: '',
    contact_phone_ext: '',
    service_hours_from: '',
    service_hours_to: '',
    number_of_docks: '',
    notes: '',
    stop_instructions: '',
  });

  useEffect(() => {
    if (facilityId) {
      loadFacility();
    }
  }, [facilityId]);

  async function loadFacility() {
    try {
      const { data, error } = await supabase
        .from('facility_locations')
        .select('*')
        .eq('id', facilityId)
        .single();

      if (error) throw error;
      
      if (data) {
        setFormData({
          facility_id: data.facility_id || '',
          name: data.name || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zip || '',
          type: data.type || 'Warehouse',
          contact_name: data.contact_name || '',
          email: data.email || '',
          phone: data.phone || '',
          contact_phone_ext: data.contact_phone_ext || '',
          service_hours_from: data.service_hours_from || '',
          service_hours_to: data.service_hours_to || '',
          number_of_docks: data.number_of_docks?.toString() || '',
          notes: data.notes || '',
          stop_instructions: data.stop_instructions || '',
        });
      }
    } catch (error) {
      console.error('Error loading facility:', error);
      toast.error('Failed to load facility');
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.city || !formData.state || !formData.zip) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);

    try {
      const dataToSave = {
        ...formData,
        number_of_docks: formData.number_of_docks ? parseInt(formData.number_of_docks) : null,
      };

      if (facilityId) {
        // Update existing facility
        const { error } = await supabase
          .from('facility_locations')
          .update({
            ...dataToSave,
            updated_at: new Date().toISOString(),
          })
          .eq('id', facilityId);

        if (error) throw error;
        toast.success('Facility updated successfully!');
      } else {
        // Create new facility
        const { error } = await supabase
          .from('facility_locations')
          .insert([dataToSave]);

        if (error) throw error;
        toast.success('Facility added successfully!');
      }
      
      onSuccess();
    } catch (error: any) {
      console.error('Error saving facility:', error);
      toast.error(error.message || 'Failed to save facility');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const formContent = (
    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Basic Information</h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Address</h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Street Address
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="e.g., 280 Lynchburg Highway"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                City *
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                State *
              </label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Zip Code *
              </label>
              <input
                type="text"
                value={formData.zip}
                onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Contact Name
            </label>
            <input
              type="text"
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Phone
            </label>
            <div className="flex gap-2">
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="text"
                placeholder="ext"
                value={formData.contact_phone_ext}
                onChange={(e) => setFormData({ ...formData, contact_phone_ext: e.target.value })}
                className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Service Info */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Service Info</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Service hours from
            </label>
            <input
              type="time"
              value={formData.service_hours_from}
              onChange={(e) => setFormData({ ...formData, service_hours_from: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="HH:mm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Service hours to
            </label>
            <input
              type="time"
              value={formData.service_hours_to}
              onChange={(e) => setFormData({ ...formData, service_hours_to: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="HH:mm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Number of docks
            </label>
            <input
              type="number"
              value={formData.number_of_docks}
              onChange={(e) => setFormData({ ...formData, number_of_docks: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              min="0"
            />
          </div>
        </div>
      </div>

      {/* Notes and Instructions */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Additional Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Note
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              maxLength={2000}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add any additional notes..."
            />
            <p className="text-xs text-slate-500 mt-1">
              {formData.notes.length}/2000 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Stop instructions
            </label>
            <textarea
              value={formData.stop_instructions}
              onChange={(e) => setFormData({ ...formData, stop_instructions: e.target.value })}
              maxLength={2000}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add special instructions for stops..."
            />
            <p className="text-xs text-slate-500 mt-1">
              {formData.stop_instructions.length}/2000 characters
            </p>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : facilityId ? 'Update Facility' : 'Add Facility'}
        </button>
      </div>
    </form>
  );

  if (!isModal) {
    return formContent;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div>
            <h2 className="text-2xl font-bold">
              {facilityId ? 'Edit Facility Location' : 'Add Facility Location'}
            </h2>
            <p className="text-sm text-blue-100 mt-1">
              {facilityId ? 'Update facility information' : 'Enter facility information'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {formContent}
      </div>
    </div>
  );
}
